import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';
import { cloudTerminalService, type CloudTerminalModelConfig, type CloudTerminalSessionInfo } from '../../services/cloud-terminal-service.js';
import type { TypeSelectedDetail } from './cp-cloud-terminal-type-picker.js';
import type { ProjectSelectedDetail } from './cp-cloud-terminal-project-picker.js';

type SessionState = 'type-select' | 'project-select' | 'model-select' | 'connecting' | 'connected' | 'disconnected' | 'error';

const FALLBACK_MODEL: CloudTerminalModelConfig = {
  provider: 'anthropic',
  model: 'claude-sonnet-4-20250514',
  displayName: 'Claude Code',
};

const CREATE_RESPONSE_TIMEOUT_MS = 15_000;

@customElement('cp-cloud-terminal-session')
export class CpCloudTerminalSession extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) session: CloudTerminalSessionInfo | null = null;
  @property({ type: Boolean }) active = false;

  @state() private _state: SessionState = 'type-select';
  @state() private _error = '';
  @state() private _requestId = '';
  @state() private _selectedType: 'claude' | 'shell' | null = null;
  @state() private _selectedCwd: string | null = null;

  private _wsHandler = this._onWsMessage.bind(this);
  private _createTimeoutId: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._clearCreateTimeout();
  }

  private _armCreateTimeout(requestId: string): void {
    this._clearCreateTimeout();
    this._createTimeoutId = window.setTimeout(() => {
      if (this._requestId === requestId && this._state === 'connecting') {
        this._state = 'error';
        this._error = 'Backend antwortet nicht (Timeout nach 15s). Bitte erneut versuchen.';
        this._requestId = '';
      }
      this._createTimeoutId = null;
    }, CREATE_RESPONSE_TIMEOUT_MS);
  }

  private _clearCreateTimeout(): void {
    if (this._createTimeoutId !== null) {
      window.clearTimeout(this._createTimeoutId);
      this._createTimeoutId = null;
    }
  }

  protected override updated(changed: Map<string, unknown>): void {
    if (changed.has('session') && this.session) {
      if (this.session.status === 'running') {
        this._state = 'connected';
      } else if (this.session.status === 'creating' && this.session.cwd) {
        this._autoCreateForProject();
      }
    }
  }

  private async _autoCreateForProject(): Promise<void> {
    if (!this.session?.cwd) return;
    this._state = 'connecting';
    this._error = '';

    const model: CloudTerminalModelConfig =
      cloudTerminalService.getLastUsedModel() ?? FALLBACK_MODEL;

    const requestId = await cloudTerminalService.createSession('claude', model, this.session.cwd, this.session?.agentId);
    this._requestId = requestId;
    this._armCreateTimeout(requestId);
  }

  private _onTypeSelected(e: CustomEvent<TypeSelectedDetail>): void {
    this._selectedType = e.detail.type;
    this._state = 'project-select';
  }

  private async _onProjectSelected(e: CustomEvent<ProjectSelectedDetail>): Promise<void> {
    this._selectedCwd = e.detail.cwd;

    if (this._selectedType === 'shell') {
      this._state = 'connecting';
      this._error = '';
      const requestId = await cloudTerminalService.createSession(
        'shell',
        undefined,
        this._selectedCwd ?? undefined,
        this.session?.agentId,
      );
      this._requestId = requestId;
      this._armCreateTimeout(requestId);
    } else {
      this._state = 'model-select';
    }
  }

  private async _onModelSelected(e: CustomEvent<CloudTerminalModelConfig>): Promise<void> {
    this._state = 'connecting';
    this._error = '';
    const requestId = await cloudTerminalService.createSession(
      'claude',
      e.detail,
      this._selectedCwd ?? undefined,
      this.session?.agentId,
    );
    this._requestId = requestId;
    this._armCreateTimeout(requestId);
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: Record<string, unknown> };

    switch (msg.type) {
      case 'cloud-terminal:created': {
        const payload = msg.payload as { sessionId: string; type: string; requestId?: string };
        if (payload.requestId && payload.requestId === this._requestId) {
          this._clearCreateTimeout();
          this._state = 'connected';
          this._requestId = '';

          const sessionInfo: CloudTerminalSessionInfo = {
            id: payload.sessionId,
            type: payload.type as 'claude' | 'shell',
            status: 'running',
            createdAt: new Date().toISOString(),
          };

          cloudTerminalService.saveSession(sessionInfo);

          this.dispatchEvent(new CustomEvent('session-connected', {
            detail: sessionInfo,
            bubbles: true, composed: true,
          }));
        }
        break;
      }

      case 'cloud-terminal:error': {
        const payload = msg.payload as { requestId?: string; error: string; sessionId?: string };
        if ((payload.requestId && payload.requestId === this._requestId) ||
            (payload.sessionId && this.session?.id === payload.sessionId)) {
          this._clearCreateTimeout();
          this._state = 'error';
          this._error = payload.error;
          this._requestId = '';
        }
        break;
      }

      case 'cloud-terminal:closed': {
        const payload = msg.payload as { sessionId: string };
        if (this.session && payload.sessionId === this.session.id) {
          this._state = 'disconnected';
        }
        break;
      }
    }
  }

  private _retry(): void {
    this._state = 'type-select';
    this._error = '';
    this._selectedType = null;
    this._selectedCwd = null;
  }

  render() {
    const displayClass = this.active ? 'cp-cloud-terminal-session--active' : '';

    return html`
      <div class="cp-cloud-terminal-session ${displayClass}">
        ${this._state === 'type-select' ? html`
          <div class="cp-cloud-terminal-session__overlay">
            <cp-cloud-terminal-type-picker @type-selected=${this._onTypeSelected}></cp-cloud-terminal-type-picker>
          </div>
        ` : nothing}

        ${this._state === 'project-select' ? html`
          <div class="cp-cloud-terminal-session__overlay">
            <cp-cloud-terminal-project-picker @project-selected=${this._onProjectSelected}></cp-cloud-terminal-project-picker>
          </div>
        ` : nothing}

        ${this._state === 'model-select' ? html`
          <div class="cp-cloud-terminal-session__overlay">
            <cp-cloud-terminal-model-picker @model-selected=${this._onModelSelected}></cp-cloud-terminal-model-picker>
          </div>
        ` : nothing}

        ${this._state === 'connecting' ? html`
          <div class="cp-cloud-terminal-session__overlay">
            <div class="cp-cloud-terminal-session__loading">
              <div class="cp-cloud-terminal-session__spinner"></div>
              <span>Session wird erstellt...</span>
            </div>
          </div>
        ` : nothing}

        ${this._state === 'error' ? html`
          <div class="cp-cloud-terminal-session__overlay">
            <div class="cp-cloud-terminal-session__error-box">
              <div class="cp-cloud-terminal-session__error-title">Fehler</div>
              <div class="cp-cloud-terminal-session__error-msg">${this._error}</div>
              <button class="cp-cloud-terminal-session__retry" @click=${this._retry}>Erneut versuchen</button>
            </div>
          </div>
        ` : nothing}

        ${this._state === 'connected' || this._state === 'disconnected' ? html`
          <cp-cloud-terminal
            .sessionId=${this.session?.id ?? ''}
            .active=${this.active}
          ></cp-cloud-terminal>
        ` : nothing}

        ${this._state === 'disconnected' ? html`
          <div class="cp-cloud-terminal-session__disconnected-bar">
            Session beendet
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cloud-terminal-session': CpCloudTerminalSession;
  }
}
